/* eslint-disable jsx-a11y/aria-role */
import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS } from '@contentful/rich-text-types';

import InputSlider from '../../Core/InputSlider';
import ToolTip from '../../Core/ToolTip';
import RichText from '../../Core/RichText';
import CurrencyFormatter from '../../Core/CurrencyFormatter';
import { ResponsiveContext } from '../../Providers/Responsive';

import {
  Section,
  StyledContainer,
  Heading,
  StyledSticky,
  Wrapper,
  InitialWrapper,
  TotalsContainer,
  SliderTotalContainer,
  CreditTotalsContainer,
  CreditCardContainer,
  CreditCard,
  CalculationTotalContainer,
  Total,
  FootnoteContainer,
  DetailsWrapper,
  SlidersContainer,
  Icon,
  BodyCopy,
  CalculationDetails,
  StyledCtas,
} from './styles';

const textRole = {
  renderNode: {
    /*
     * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
     * https://github.com/dequelabs/axe-core/milestone/19
     * https://github.com/dequelabs/axe-core/issues/1597
     */
    [BLOCKS.PARAGRAPH]: (_, children) => <p role="text">{children}</p>,
  },
};

const body = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <BodyCopy className="small-copy">{children}</BodyCopy>
    ),
  },
};

const CalculatorSingular = ({ data }) => {
  // * Null check for data
  if (!data) return null;
  const intl = useIntl();

  // * Destructure data values
  const {
    heading,
    pointsText,
    pointsSubtext,
    purchasesText,
    worthText,
    products,
    sliderSection,
    defaultSelectedCard,
    ctas,
  } = data;

  // * Create the default state value for all the sliders
  // * The state object looks like { sliderField: sliderValue }
  const defaultState = activeCard => {
    const obj = {};
    const ptsObj = {};
    sliderSection.map(
      ({
        sliderLabel,
        ppdMastercard,
        ppdWorld,
        ppdWorldElite,
        avgMonthlySpendingMastercard,
        avgMonthlySpendingWorld,
        avgMonthlySpendingWorldElite,
      }) => {
        const key = sliderLabel.split(' ').join('-').toLocaleLowerCase();
        ptsObj[key] = {
          'pc-financial-mastercard': avgMonthlySpendingMastercard || 0,
          'pc-financial-world-mastercard': avgMonthlySpendingWorld || 0,
          'pc-financial-world-elite-mastercard':
            avgMonthlySpendingWorldElite || 0,
        };
        obj[key] = {
          'pc-financial-mastercard': ppdMastercard,
          'pc-financial-world-mastercard': ppdWorld,
          'pc-financial-world-elite-mastercard': ppdWorldElite,
          value: ptsObj[key][activeCard],
          points: 0,
        };
        return true;
      },
    );
    obj.monthlyValues = {
      value: 2100,
      avgPoints: 371000,
    };
    return obj;
  };

  const activeCard = defaultSelectedCard;
  let offerValue = 0;

  // * sliders that will only display the value or body copy
  const staticSliders = sliderSection.filter(slider => slider.bodyCopy);

  if (staticSliders.length > 0) {
    staticSliders.map(slider => {
      const {
        avgMonthlySpendingMastercard,
        avgMonthlySpendingWorld,
        avgMonthlySpendingWorldElite,
      } = slider;
      switch (defaultSelectedCard) {
        case 'pc-financial-mastercard':
          offerValue += avgMonthlySpendingMastercard;
          break;
        case 'pc-financial-world-mastercard':
          offerValue += avgMonthlySpendingWorld;
          break;
        case 'pc-financial-world-elite-mastercard':
          offerValue += avgMonthlySpendingWorldElite;
          break;
        default:
          break;
      }
      return true;
    });
  }
  // * State for sliders and their values
  const [sliderValue, setSliderValue] = useState(defaultState(activeCard));
  // * State for the total monthly purchases (all slider values added together)
  const [monthlyPurchaseTotal, setMonthlyPurchaseTotal] = useState(0);
  // * State for the total points based on purchases (all slider values added together)
  const [pointsTotal, setPointsTotal] = useState(0);
  // * State to track if the calculator is fixed or static
  const [fixed, setFixed] = useState(false);
  // * State to track mobile view to render sticky header
  const { mobile } = useContext(ResponsiveContext);

  // * This func updates the state value for 1 slider. It requires a 'state key', which is the field name of the slider
  const updateSliderValue = (stateKey, price) => {
    setSliderValue(prevState => {
      const newState = {};
      const pointValue = price * prevState[stateKey][activeCard] || 0;
      const innerState = { value: price, points: pointValue };
      newState[stateKey] = { ...prevState[stateKey], ...innerState };
      // * Get name and current value of a slider when it is changed
      return { ...prevState, ...newState };
    });
  };

  // * Need to recalculate all point values based on the current price/value of each slider. Then call calculate
  const updateSliderPoints = () => {
    const newState = { ...sliderValue };
    const keys = Object.keys(newState);
    // * Set all slider points to 0, calculate the new point total based on the price, and the active card, and points per dollar for the active card
    keys.map(key => {
      newState[key].points =
        0 + sliderValue[key].value * sliderValue[key][activeCard] || 0;
      return true;
    });
    setSliderValue(prevState => {
      return { ...prevState, ...newState };
    });
  };

  // * Totals up all the purchase amount, and the total points
  const calculate = () => {
    // * Reset the purchases and points to 0, so we dont need to deal with negatives
    let totalPurchases = 0;
    let totalPoints = 0;
    // filter out total monthly spending object inside the slidervalue object
    const keys = Object.keys(sliderValue).filter(
      key => key !== 'monthlyValues',
    );
    // * Total up all the purchases and the points of each slider
    keys.map(key => {
      totalPurchases += sliderValue[key].value;
      totalPoints += sliderValue[key].points;
      return true;
    });
    // * removing the welcome bonus or offers from the total spending
    if (staticSliders.length > 0) {
      totalPurchases -= offerValue;
    }
    // * Set the purchase total in state
    setMonthlyPurchaseTotal(totalPurchases);
    // * Set the price total in state
    setPointsTotal(totalPoints * 12);
  };

  // * On mount & everytime a new card is selected, update the total points in each slider based on the active card, slider value, and the points per dollar for that slider (depends on active card)
  useEffect(() => {
    updateSliderPoints();
  }, [activeCard]);

  // * On mount & everytime a slider value gets updated we want to collect the value of each slider and add them together to provide us with the total amount of monthly purchases. We also want to total up all the optimum points and display this annually
  useEffect(() => {
    calculate();
  }, [sliderValue]);

  return (
    <Section>
      <StyledContainer className="calcArea">
        <Heading>
          <RichText document={heading} />
        </Heading>
        <Wrapper>
          <InitialWrapper>
            <CalculationDetails>
              <TotalsContainer>
                <SliderTotalContainer>
                  <p>{purchasesText}</p>
                  <span>
                    <CurrencyFormatter
                      value={
                        monthlyPurchaseTotal >= 0
                          ? monthlyPurchaseTotal
                          : sliderValue.monthlyValues.value
                      }
                      lang={intl.locale}
                    />
                  </span>
                </SliderTotalContainer>
                <CreditTotalsContainer>
                  <CreditCardContainer>
                    <CreditCard>
                      <img
                        src={products[0].imageDam.asset.localImage.publicURL}
                        alt={
                          products[0].imageDam.altText || ''
                            ? products[0].imageDam.altText || ''
                            : ''
                        }
                      />
                      <RichText
                        document={products[0].text}
                        options={textRole}
                      />
                    </CreditCard>
                  </CreditCardContainer>
                  <StyledSticky
                    boundaryElement=".calcArea"
                    topOffset={70}
                    bottomOffset={0}
                    onFixedToggle={isFixed => setFixed(isFixed)}
                    fixed={fixed.toString()}
                    disabled={!mobile}
                    stickyStyle={{ paddingTop: '70px', zIndex: '100' }}
                  >
                    <CalculationTotalContainer role="text">
                      <p className="worth">{worthText}</p>
                      <Total>
                        <div className="points">
                          <CurrencyFormatter
                            value={
                              pointsTotal >= 0
                                ? pointsTotal / 1000 + offerValue
                                : 0
                            }
                            lang={intl.locale}
                          />
                        </div>
                        <p className="points-breakdown">
                          {ReactHtmlParser(pointsText)}
                        </p>
                      </Total>
                    </CalculationTotalContainer>
                  </StyledSticky>
                </CreditTotalsContainer>
              </TotalsContainer>
              <FootnoteContainer>
                <p>{ReactHtmlParser(pointsSubtext)}</p>
              </FootnoteContainer>
            </CalculationDetails>
          </InitialWrapper>
          <DetailsWrapper>
            <SlidersContainer>
              <ul>
                {sliderSection.map(
                  ({
                    id,
                    sliderIconDam,
                    sliderLabel,
                    sliderTooltip,
                    bodyCopy,
                  }) => {
                    const sliderIconUrl =
                      sliderIconDam.asset.localImage.publicURL;

                    const stateKey = sliderLabel
                      .split(' ')
                      .join('-')
                      .toLocaleLowerCase();
                    return (
                      <li key={id} className={stateKey}>
                        <div className="title">
                          <Icon src={sliderIconUrl} alt="" />
                          <p>{ReactHtmlParser(sliderLabel)}</p>
                          {sliderTooltip && (
                            <ToolTip
                              id={id}
                              tipData={sliderTooltip}
                              labelId={sliderLabel}
                              ariaLabel={`${intl.formatMessage({
                                id: 'toolTip',
                              })} ${sliderLabel}: ${sliderTooltip}.`}
                            >
                              ?
                            </ToolTip>
                          )}
                        </div>
                        {bodyCopy ? (
                          <RichText document={bodyCopy} options={body} />
                        ) : (
                          <InputSlider
                            key={id}
                            state={sliderValue[stateKey].value}
                            stateKey={stateKey}
                            updateState={updateSliderValue}
                            label={sliderLabel}
                            step={50}
                          />
                        )}
                      </li>
                    );
                  },
                )}
              </ul>
            </SlidersContainer>
          </DetailsWrapper>
        </Wrapper>
      </StyledContainer>
      {ctas && <StyledCtas ctas={ctas} direction="row" />}
    </Section>
  );
};

CalculatorSingular.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default CalculatorSingular;
